/* ----------------- React --------------- */
import { useCallback, useState } from "react";

/* ----------------- Types --------------- */
import type { Distribution } from "@memorang/types";

import { useExamContext } from "@features/exam";
import { useProethicsFlowStore } from "@features/proethics-simulation";
/* ----------------- Hooks --------------- */
import useSessionStore from "./useSessionStore";

/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";

/* ----------------- Constants --------------- */
import { events } from "@constants/tracking";

import {
	getMockedSessionResponse,
	getProethicaLiveCreateParams,
	getProethicaTestCreateParams,
	liveSessionEnabled,
	normalizeProethicaSessionResponse,
} from "@constants/demo";
import { getProethicaTestDescriptor } from "@constants/demo/proethica-tests";
import { isWeb } from "@helpers/platform";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { flattenSessionItems } from "@helpers/session";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import type { SessionResponse } from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useWorkflowStore from "components/WorkflowItem/useWorkflowStore";
/* ----------------- Router --------------- */
import { useRouter } from "expo-router";
import { createSession, endSession } from "../api/mutations";
import type { CreateSessionRequest } from "../api/types";
import { useEMRStore } from "../components/emr-card/useEMRStore";
import useAIFeedbackStore from "./useAIFeedbackStore";
import { useProethicaSessionStore } from "./useProethicaSessionStore";
import { useResumeSession } from "./useResumeSession";

export enum LearningModes {
	questions = "question",
	writing = "writing",
	listening = "listening",
	speaking = "speaking",
	reading = "reading",
	flashcards = "flashcards",
	quiz = "quiz",
}

export const useSession = () => {
	const {
		currentExamContext: { studyPackProductId },
	} = useExamContext();
	const router = useRouter();

	const { showToast } = useToast();
	const { handleResumeSession } = useResumeSession();

	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const updateSession = useSessionStore((state) => state.updateSession);
	const resetAiFeedbackStore = useAIFeedbackStore(
		(state) => state.resetAiFeedbackStore,
	);

	const resetWorkflowStore = useWorkflowStore(
		(state) => state.resetWorkflowStore,
	);
	const resetEMRStore = useEMRStore((state) => state.resetUserData);
	const { currentExam } = useDemoExamStore();
	const examId = currentExam?.id;

	const { organizationId, projectId, collectionId, userId } = useAppConfigIds();

	const queryClient = useQueryClient();
	const updateEndingSessionInProgress = useSessionStore(
		(state) => state.updateEndingSessionInProgress,
	);

	const toggleShowErrorDialog = () => {
		setShowErrorDialog((prev) => !prev);
	};

	const createSessionMutation = useMutation({
		mutationFn: createSession,
	});

	const endSessionMutation = useMutation({
		mutationFn: endSession,
		onSuccess: () => {
			updateEndingSessionInProgress(false);
			queryClient.invalidateQueries();
		},
	});

	const createNewSession = useCallback(
		(request: CreateSessionRequest) => {
			return createSessionMutation.mutateAsync(request);
		},
		[createSessionMutation],
	);

	const endCurrentSession = ({
		id,
		userId,
	}: { id: number; userId: number }) => {
		if (!liveSessionEnabled(examId)) {
			updateEndingSessionInProgress(false);
			return;
		}
		// Finishing/submitting marks the module complete so the list shows "View
		// Report"; re-entry re-checks the backend status and self-corrects.
		const moduleId = useDemoExamStore.getState().currentModuleId;
		const tracked = moduleId
			? useProethicaSessionStore.getState().sessionByModule[moduleId]
			: undefined;
		if (moduleId && tracked) {
			useProethicaSessionStore.getState().setModuleSession(moduleId, {
				numericId: tracked.numericId,
				status: "COMPLETE",
			});
		}
		updateEndingSessionInProgress(true);
		return endSessionMutation.mutateAsync({ sessionId: id, userId });
	};

	const handlePostSessionCreation = ({
		newSessionResponse,
		numItemsRequested,
		dist,
		tags,
		handleClose,
		push,
		isDiagnostic,
	}: {
		newSessionResponse: SessionResponse;
		numItemsRequested: number;
		dist: Distribution;
		tags?: number[] | string[];
		handleClose?: () => void;
		push?: boolean;
		isDiagnostic?: boolean;
	}) => {
		trackCustomEvent({
			eventName: events.sessionStarted,
			numItems: numItemsRequested,
			dist: JSON.stringify(dist),
			tags: JSON.stringify(tags || []),
			contentId: studyPackProductId,
			type: isDiagnostic ? "DIAGNOSTIC_SESSION" : "FORMATIVE_SESSION",
		});
		updateSession({
			sessionResponse: newSessionResponse,
			numItemsRequested,
			isSummativeTest: false,
			isDiagnostic,
		});
		if (handleClose || push) {
			handleClose?.();
			router.push({
				pathname: "/(protected)/session/[id]",
				params: {
					id: newSessionResponse.id,
				},
			});
		} else {
			router.push({
				pathname: "/(protected)/session/[id]",
				params: {
					id: newSessionResponse.id,
				},
			});
		}
	};

	const getSessionResponse = useCallback(() => {
		// if (isWeb || isAiDemoEmail) {
		const selectedModuleId = useDemoExamStore.getState().currentModuleId;
		// Converge mock and live on the same shape: live ProEthica items become
		// legacy SessionItems here, so the filtering below and every downstream
		// consumer see one format.
		const mockedSessionResponse = normalizeProethicaSessionResponse(
			getMockedSessionResponse(examId, selectedModuleId),
		);

		if (isWeb) {
			return mockedSessionResponse;
		}

		const filteredSessionItems = mockedSessionResponse.sessionItems.map(
			(item) => {
				return {
					...item,
					items: item.items?.filter(
						(item) => item.metadata?.type !== "MARKING_ANSWER",
					),
				};
			},
		);

		return {
			...mockedSessionResponse,
			sessionItems: filteredSessionItems,
		};
	}, [examId]);

	const resumeTrackedModuleSession = useCallback(
		async (moduleId: string): Promise<boolean> => {
			const tracked =
				useProethicaSessionStore.getState().sessionByModule[moduleId];
			if (!tracked) {
				return false;
			}
			const outcome = await handleResumeSession(tracked.numericId);
			if (outcome === "failed") {
				useProethicaSessionStore.getState().clearModuleSession(moduleId);
				return false;
			}
			useProethicaSessionStore.getState().setModuleSession(moduleId, {
				numericId: tracked.numericId,
				status: outcome === "report" ? "COMPLETE" : "ACTIVE",
			});
			return true;
		},
		[handleResumeSession],
	);

	const buildCreateParams = useCallback(
		(args: {
			live: boolean;
			selectedModuleId?: string;
			numItemsRequested: number;
			dist: Distribution;
			tags?: number[] | string[];
			itemIds?: number[];
			additionalSessionParams?: {
				title: string;
				icon: string;
				sessionSubType?: "DIAGNOSTIC";
				examId?: string;
			};
		}): CreateSessionRequest => {
			if (args.live) {
				return getProethicaLiveCreateParams(args.selectedModuleId);
			}
			const { additionalSessionParams } = args;
			return {
				userId,
				collectionId,
				projectId,
				organizationId,
				sessionType: additionalSessionParams ? "SUMMATIVE" : "FORMATIVE",
				sessionSubType: additionalSessionParams?.sessionSubType,
				tagIds: (args.tags as number[]) || [],
				itemIds: args.itemIds,
				itemsSelected: args.numItemsRequested,
				itemsSelectedDistribution: args.dist,
				title: additionalSessionParams?.title,
				icon: additionalSessionParams?.icon,
				examId: additionalSessionParams?.examId,
			};
		},
		[userId, collectionId, projectId, organizationId],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const handleCreateFormativeSession = useCallback(
		async ({
			dist,
			tags,
			handleClose,
			itemIds,
			push,
			additionalSessionParams,
		}: {
			dist: Distribution;
			tags?: number[] | string[];
			handleClose?: () => void;
			itemIds?: number[];
			push?: boolean;
			additionalSessionParams?: {
				title: string;
				icon: string;
				sessionSubType?: "DIAGNOSTIC";
				examId?: string;
			};
		}) => {
			resetSessionStore();
			resetAiFeedbackStore();
			resetWorkflowStore();
			resetEMRStore();
			useProethicsFlowStore.getState().reset();

			const live = liveSessionEnabled(examId);
			const selectedModuleId = useDemoExamStore.getState().currentModuleId;

			// Re-entering a once-only practice test: the backend rejects a second
			// create. Resume the tracked session (or open its report) instead of
			// creating, mirroring main's status-based routing.
			if (
				live &&
				selectedModuleId &&
				(await resumeTrackedModuleSession(selectedModuleId))
			) {
				return;
			}

			try {
				const numItemsRequested = Object.values(dist).reduce(
					(acc, curr) => acc + curr,
					0,
				);

				const params = buildCreateParams({
					live,
					selectedModuleId,
					numItemsRequested,
					dist,
					tags,
					itemIds,
					additionalSessionParams,
				});

				const result = live
					? normalizeProethicaSessionResponse(await createNewSession(params))
					: getSessionResponse();

				const createErrors = (
					result as { errors?: { title?: string; message?: string } }
				).errors;
				if (createErrors || !result.sessionItems?.length) {
					showToast({
						message:
							createErrors?.message ?? "Failed to create session, try again",
					});
					return;
				}

				if (live && selectedModuleId && result.numericId) {
					useProethicaSessionStore
						.getState()
						.setModuleSession(selectedModuleId, {
							numericId: result.numericId,
							status: "ACTIVE",
						});
				}

				const flattenedSessionItems = flattenSessionItems(result.sessionItems);

				// const newSessionResponse = finalMockedSessionResponse;

				const newSessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};

				handlePostSessionCreation({
					newSessionResponse,
					numItemsRequested,
					dist,
					tags,
					handleClose,
					push,
					isDiagnostic:
						additionalSessionParams?.sessionSubType === "DIAGNOSTIC",
				});
			} catch (_error) {
				toggleShowErrorDialog();
				captureErrorToSentry(new Error("Error creating formative session"), {
					error: JSON.stringify(_error),
				});
			}
		},
		[
			resetSessionStore,
			router,
			studyPackProductId,
			updateSession,
			createNewSession,
			resumeTrackedModuleSession,
			buildCreateParams,
		],
	);

	const defaultDist: Distribution = { low: 0, medium: 0, high: 0, none: 10 };

	const handleDirectSessionStart = useCallback(
		({ push, moduleId }: { push?: boolean; moduleId?: string } = {}) => {
			useDemoExamStore
				.getState()
				.setCurrentModuleId(examId === "proethica" ? moduleId : undefined);
			return handleCreateFormativeSession({
				dist: defaultDist,
				tags: [],
				push,
			});
		},
		[examId, handleCreateFormativeSession],
	);

	// Pre/post tests run as a summative, single-submit session sourced from the
	// test envelope (mock now, live create-session once isLiveSession flips).
	// Each start is a fresh session - tests are independent, retake-friendly
	// entry points, so unlike modules they are not resumed from the tracker.
	// biome-ignore lint/correctness/useExhaustiveDependencies: toggleShowErrorDialog is recreated each render; mirrors handleCreateFormativeSession.
	const handleStartProethicaTest = useCallback(
		async ({ testId }: { testId: string }) => {
			const descriptor = getProethicaTestDescriptor(testId);
			if (!descriptor) {
				return;
			}

			resetSessionStore();
			resetAiFeedbackStore();
			resetWorkflowStore();
			resetEMRStore();
			useProethicsFlowStore.getState().reset();
			useDemoExamStore.getState().setCurrentModuleId(testId);

			const live = liveSessionEnabled(examId);
			try {
				const result = live
					? normalizeProethicaSessionResponse(
							await createNewSession(
								getProethicaTestCreateParams(descriptor.kind),
							),
						)
					: normalizeProethicaSessionResponse(
							getMockedSessionResponse(examId, testId),
						);

				const createErrors = (
					result as { errors?: { title?: string; message?: string } }
				).errors;
				if (createErrors || !result.sessionItems?.length) {
					showToast({
						message: createErrors?.message ?? "Failed to start test, try again",
					});
					return;
				}

				const newSessionResponse = {
					...result,
					sessionItems: flattenSessionItems(result.sessionItems),
				};

				trackCustomEvent({
					eventName: events.sessionStarted,
					numItems: descriptor.numItems,
					dist: JSON.stringify({ none: descriptor.numItems }),
					tags: JSON.stringify([]),
					contentId: studyPackProductId,
					type: "SUMMATIVE_SESSION",
				});

				updateSession({
					sessionResponse: newSessionResponse,
					numItemsRequested: descriptor.numItems,
					isSummativeTest: true,
					proethicaTestKind: descriptor.kind,
				});

				router.push({
					pathname: "/(protected)/session/[id]",
					params: {
						id: newSessionResponse.id,
					},
				});
			} catch (_error) {
				toggleShowErrorDialog();
				captureErrorToSentry(
					new Error("Error starting proethica test session"),
					{
						error: JSON.stringify(_error),
					},
				);
			}
		},
		[
			examId,
			studyPackProductId,
			resetSessionStore,
			resetAiFeedbackStore,
			resetWorkflowStore,
			resetEMRStore,
			createNewSession,
			updateSession,
			router,
			showToast,
		],
	);

	return {
		handleCreateFormativeSession,
		handleDirectSessionStart,
		handleStartProethicaTest,
		showErrorDialog,
		toggleShowErrorDialog,
		createNewSession,
		endCurrentSession,
		isCreatingSession: createSessionMutation.isPending,
		isEndingSession: endSessionMutation.isPending,
		createSessionError: createSessionMutation.error,
		endSessionError: endSessionMutation.error,
	};
};
